import { DEFAULT_PREVIEW_3D_CONFIG, type Preview3DConfig } from '../config/preview3d'
import { getPreview3dMaterialPreset } from '../config/preview3d.materials'
import { EDITOR_CONFIG_DEFAULTS, type EditorMode, type ResolvedEditorConfig } from '../config/editorConfig.defaults'

type Dict = Record<string, any>

function isPlainObject(v: unknown): v is Dict {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

function deepMerge(base: Dict, patch: Dict): Dict {
  const out: Dict = { ...base }
  for (const [k, v] of Object.entries(patch || {})) {
    if (v === undefined) continue
    if (Array.isArray(v)) {
      out[k] = [...v]
      continue
    }
    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = deepMerge(out[k], v)
      continue
    }
    if (isPlainObject(v)) {
      out[k] = deepMerge({}, v)
      continue
    }
    out[k] = v
  }
  return out
}

function pickPreviewConfig(raw: any): Partial<Preview3DConfig> {
  if (!isPlainObject(raw)) return {}

  const keys = new Set(Object.keys(DEFAULT_PREVIEW_3D_CONFIG))
  const nested = isPlainObject(raw.preview3d) ? raw.preview3d : null
  if (nested) return nested as Partial<Preview3DConfig>

  const flat: Dict = {}
  for (const [k, v] of Object.entries(raw)) {
    if (keys.has(k)) flat[k] = v
  }
  return flat as Partial<Preview3DConfig>
}

function pickPreviewPreset(raw: any): string | null {
  if (!isPlainObject(raw)) return null

  const nested = isPlainObject(raw.preview3d) ? raw.preview3d : null
  const source = nested || raw

  const key = source.preset ?? source.materialPreset ?? source.material ?? null
  if (!key) return null

  const value = String(key).trim()
  return value || null
}

function bool(v: unknown, fallback: boolean): boolean {
  return v === undefined || v === null ? fallback : !!v
}
function int(v: unknown, fallback: number): number {
  const n = Number(v)
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : fallback
}
function nonNeg(v: unknown, fallback: number): number {
  const n = Number(v)
  return Number.isFinite(n) ? Math.max(0, Math.round(n * 100) / 100) : fallback
}
function intList(v: unknown): number[] {
  return Array.isArray(v) ? [...new Set(v.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))] : []
}
function strList(v: unknown): string[] {
  return Array.isArray(v) ? [...new Set(v.map((x) => String(x ?? '').trim()).filter(Boolean))] : []
}

/**
 * Typisierte Editor-Konfiguration (mode, features, limits, canvas, …) aus einem bereits
 * gemergten Objekt. Unbekannte Schlüssel werden verworfen, Typen erzwungen.
 */
export function normalizeEditorConfig(raw: unknown): ResolvedEditorConfig {
  const r = isPlainObject(raw) ? raw : {}
  const d = EDITOR_CONFIG_DEFAULTS
  const modeRaw = String(r.mode ?? d.mode).trim().toLowerCase()
  const mode: EditorMode = modeRaw === 'design' ? 'design' : 'simple'
  const f = isPlainObject(r.features) ? r.features : {}
  const l = isPlainObject(r.limits) ? r.limits : {}
  const c = isPlainObject(r.canvas) ? r.canvas : {}
  const shapeRaw = String(c.shape ?? d.canvas.shape).trim().toLowerCase()
  const shape = (['rect', 'rounded', 'circle', 'hexagon', 'custom'] as const).find((s) => s === shapeRaw) ?? 'rect'

  return {
    mode,
    designAvailable: mode === 'simple' && bool(r.designAvailable, d.designAvailable),
    features: {
      layers: bool(f.layers, d.features.layers),
      text: bool(f.text, d.features.text),
      stickers: bool(f.stickers, d.features.stickers),
      templates: bool(f.templates, d.features.templates),
      collage: bool(f.collage, d.features.collage),
      filters: bool(f.filters, d.features.filters),
      backgrounds: bool(f.backgrounds, d.features.backgrounds),
      shapes: bool(f.shapes, d.features.shapes),
      freeColor: bool(f.freeColor, d.features.freeColor),
    },
    limits: {
      maxLayers: int(l.maxLayers, d.limits.maxLayers),
      maxTextLayers: int(l.maxTextLayers, d.limits.maxTextLayers),
      maxImageLayers: int(l.maxImageLayers, d.limits.maxImageLayers),
      minDpiWarn: int(l.minDpiWarn, d.limits.minDpiWarn),
      minDpiBlock: int(l.minDpiBlock, d.limits.minDpiBlock),
    },
    canvas: {
      shape,
      cornerRadiusMm: nonNeg(c.cornerRadiusMm, d.canvas.cornerRadiusMm),
      bleedMm: nonNeg(c.bleedMm, d.canvas.bleedMm),
      safeMarginMm: nonNeg(c.safeMarginMm, d.canvas.safeMarginMm),
      dpi: Math.max(72, Math.min(600, int(c.dpi, d.canvas.dpi) || d.canvas.dpi)),
      roundedOptionRadiusMm: nonNeg(c.roundedOptionRadiusMm, d.canvas.roundedOptionRadiusMm),
      showGuides: bool(c.showGuides, d.canvas.showGuides),
      snapEnabled: bool(c.snapEnabled, d.canvas.snapEnabled),
      collageGutterMm: nonNeg(c.collageGutterMm, d.canvas.collageGutterMm),
      shapeParams: isPlainObject(c.shapeParams) ? c.shapeParams : {},
      sides: Number(c.sides) === 2 ? 2 : 1,
      zones: Array.isArray(c.zones)
        ? c.zones.filter(isPlainObject).map((z) => ({
            kind: (z.kind === 'handle' || z.kind === 'seam' ? z.kind : 'hidden') as 'hidden' | 'handle' | 'seam',
            xMm: Number(z.xMm) || 0, yMm: Number(z.yMm) || 0, wMm: Math.max(0, Number(z.wMm) || 0), hMm: Math.max(0, Number(z.hMm) || 0),
            ...(z.label ? { label: String(z.label) } : {}),
          }))
        : [],
    },
    allowedTemplateCategoryIds: intList(r.allowedTemplateCategoryIds),
    allowedAssetCategoryIds: intList(r.allowedAssetCategoryIds),
    allowedFontIds: strList(r.allowedFontIds),
    defaultTemplateId: r.defaultTemplateId != null && String(r.defaultTemplateId).trim() ? String(r.defaultTemplateId).trim() : null,
    colorHintText: String(r.colorHintText ?? d.colorHintText),
    preview3d: isPlainObject(r.preview3d) ? r.preview3d : {},
    sheets: Array.isArray(r.sheets)
      ? r.sheets.filter((x: any) => isPlainObject(x) && String(x.templateId || '').trim()).map((x: any) => ({
          templateId: String(x.templateId).trim(),
          sizeIds: intList(x.sizeIds),
          optionVariantIds: intList(x.optionVariantIds),
          optionValues: strList(x.optionValues).map((v) => v.trim().toLowerCase()).filter(Boolean),
          label: String(x.label || ''),
        }))
      : [],
    sheetExport: r.sheetExport === 'sheet' || r.sheetExport === 'both' ? r.sheetExport : 'slots',
  }
}

/**
 * Passender Bogen für Größe + Optionen (Spiegel von ResolvedEditorConfig::sheetFor in der API):
 * leere Listen gelten für alle, die genaueste Zuordnung gewinnt.
 */
export function sheetForSelection(
  sheets: ResolvedEditorConfig['sheets'],
  sizeId: number | null,
  options: Array<{ variantId?: unknown; value?: unknown; title?: unknown }>,
): string | null {
  const variantIds = options.map((o) => Number(o?.variantId || 0)).filter((n) => n > 0)
  const values = options.flatMap((o) => [o?.value, o?.title]).map((v) => String(v ?? '').trim().toLowerCase()).filter(Boolean)
  let best: string | null = null
  let bestScore = -1
  for (const s of sheets) {
    if (s.sizeIds.length && !s.sizeIds.includes(Number(sizeId || 0))) continue
    const hasOptions = s.optionVariantIds.length > 0 || s.optionValues.length > 0
    if (hasOptions && !s.optionVariantIds.some((id) => variantIds.includes(id)) && !s.optionValues.some((v) => values.includes(v))) continue
    const score = (s.sizeIds.length ? 1 : 0) + (hasOptions ? 2 : 0)
    if (score > bestScore) { best = s.templateId; bestScore = score }
  }
  return best
}

/** Darf für diese Konfiguration der Designer geöffnet werden (Pflicht oder zusätzlich)? */
export function offersDesigner(config: Pick<ResolvedEditorConfig, 'mode' | 'designAvailable'> | null | undefined): boolean {
  return !!config && (config.mode === 'design' || config.designAvailable)
}

/**
 * Editor-Konfiguration für ein Produkt in einer Kategorie. Bevorzugt die serverseitig
 * gemergte `editorConfigByCategory[categoryId]` aus dem Bootstrap; fällt sonst auf den
 * client-seitigen deepMerge(Default ← Kategorie ← Produkt) zurück.
 */
export function resolveEditorConfig(input: {
  product?: { editorConfigByCategory?: Record<string, any> | null; editorOverrideConfig?: any } | null
  categoryId?: number | string | null
  categoryConfig?: any
  productConfig?: any
}): ResolvedEditorConfig {
  const byCat = input.product?.editorConfigByCategory
  const key = input.categoryId != null ? String(input.categoryId) : ''
  if (isPlainObject(byCat)) {
    const hit = (key && isPlainObject(byCat[key])) ? byCat[key] : null
    const first = !hit ? Object.values(byCat).find(isPlainObject) ?? null : null
    const chosen = hit ?? first
    if (chosen) return normalizeEditorConfig(chosen)
  }
  const merged = deepMerge(
    deepMerge(EDITOR_CONFIG_DEFAULTS as unknown as Dict, isPlainObject(input.categoryConfig) ? input.categoryConfig : {}),
    isPlainObject(input.productConfig ?? input.product?.editorOverrideConfig) ? (input.productConfig ?? input.product?.editorOverrideConfig) : {},
  )
  return normalizeEditorConfig(merged)
}

export function resolvePreview3dOverrides(input: {
  categoryConfig?: any
  productConfig?: any
}): Partial<Preview3DConfig> {
  const catPreset = getPreview3dMaterialPreset(pickPreviewPreset(input.categoryConfig))
  const prodPreset = getPreview3dMaterialPreset(pickPreviewPreset(input.productConfig))
  const cat = pickPreviewConfig(input.categoryConfig)
  const prod = pickPreviewConfig(input.productConfig)

  return deepMerge(
    deepMerge(catPreset as Dict, cat as Dict),
    deepMerge(prodPreset as Dict, prod as Dict),
  ) as Partial<Preview3DConfig>
}
